import { AxiosResponse } from 'axios';
import {
    RemoteExecutionServiceImpl,
    RemoteGETCallInputObject,
    RemotePOSTCallInputObject,
    ServerResponse,
} from '../../../remoteExecution/RemoteExecutionServiceImpl';
import { isValidWorkyServerResponseWithData } from '../../../common/WorkyAPIUtils';
import {
    AgencyJobberPhiPresignedUploadConfirmReqDTO,
    AgencyJobberPhiPresignedUploadInitiateReqDTO,
    ListJobberPhiDocumentsAgencyUserRequestDTO,
    ListOrgPortalUserPhiDocumentsAgencyUserRequestDTO,
    AgencyOrgPortalPhiPresignedUploadInitiateReqDTO,
    AgencyOrgPortalPhiPresignedUploadConfirmReqDTO,
    AgencyOrgPortalPhiSoftDeleteDocumentReqDTO,
    OrgPortalUserPhiPresignedDownloadUrlRequestDTO,
    JobberPhiPresignedDownloadUrlRequestDTO,
    AgencyJobberPhiSoftDeleteDocumentReqDTO,
    AgencyJobberPhiReviewDocumentReqDTO,
    PhiDocumentListRespDTO,
    PhiPresignedDownloadUrlRespDTO,
    PhiSoftDeleteDocumentRespDTO,
    PhiReviewDocumentRespDTO,
    PhiPresignedUploadConfirmRespDTO,
    PhiPresignedUploadInitiateRespDTO,
    JobberPhiDocumentViewersRequestDTO,
    PhiDocumentViewerListRespDTO,
    CreateTrustVaultDocumentSubTypeReqDTO,
    DisableTrustVaultDocumentSubTypeReqDTO,
    RenameTrustVaultDocumentSubTypeReqDTO,
    TrustVaultDocumentSubTypeListRespDTO,
    TrustVaultDocumentSubTypeRespDTO,
} from '../interfaces/TrustVaultPhiDocumentsAgencyUserInterfaces';

const backendApiBasePathLocal: string = process.env.REACT_APP_BACK_END_SERVER_BASE_PATH_LOCAL ?? '';
const remoteCallObject = RemoteExecutionServiceImpl();

const LIST_JOBBER_PHI_DOCUMENTS_AGENCY_PATH: string = 'v1/phi/document/agency/list/jobber/';
const INITIATE_JOBBER_PHI_UPLOAD_AGENCY_PATH: string = 'v1/phi/document/agency/upload/initiate/';
const CONFIRM_JOBBER_PHI_UPLOAD_AGENCY_PATH: string = 'v1/phi/document/agency/upload/confirm/';
const JOBBER_PHI_DOWNLOAD_URL_AGENCY_PATH: string = 'v1/phi/document/agency/download/url/jobber/';
const JOBBER_PHI_SOFT_DELETE_AGENCY_PATH: string = 'v1/phi/document/agency/delete/jobber-doc/';
const JOBBER_PHI_REVIEW_AGENCY_PATH: string = 'v1/phi/document/agency/review/jobber-doc/';
const JOBBER_PHI_DOCUMENT_VIEWERS_AGENCY_PATH: string = 'v1/phi/document/agency/viewers/jobber/';
const LIST_DOCUMENT_SUB_TYPES_AGENCY_PATH: string = 'v1/phi/document/agency/document-sub-types/';
const CREATE_DOCUMENT_SUB_TYPE_AGENCY_PATH: string = 'v1/phi/document/agency/document-sub-types/';
const DISABLE_DOCUMENT_SUB_TYPE_AGENCY_PATH: string = 'v1/phi/document/agency/document-sub-types/disable/';
const RENAME_DOCUMENT_SUB_TYPE_AGENCY_PATH: string = 'v1/phi/document/agency/document-sub-types/rename/';
const LIST_CLIENT_PATIENT_DOCUMENT_SUB_TYPES_AGENCY_PATH: string =
    'v1/phi/document/agency/client-patient/document-sub-types/';
const CREATE_CLIENT_PATIENT_DOCUMENT_SUB_TYPE_AGENCY_PATH: string =
    'v1/phi/document/agency/client-patient/document-sub-types/';
const DISABLE_CLIENT_PATIENT_DOCUMENT_SUB_TYPE_AGENCY_PATH: string =
    'v1/phi/document/agency/client-patient/document-sub-types/disable/';
const RENAME_CLIENT_PATIENT_DOCUMENT_SUB_TYPE_AGENCY_PATH: string =
    'v1/phi/document/agency/client-patient/document-sub-types/rename/';
const LIST_ORG_PORTAL_USER_PHI_DOCUMENTS_AGENCY_PATH: string = 'v1/phi/document/agency/list/org-portal-user/';
const INITIATE_ORG_PORTAL_USER_PHI_UPLOAD_AGENCY_PATH: string = 'v1/phi/document/agency/upload/org-portal/initiate/';
const CONFIRM_ORG_PORTAL_USER_PHI_UPLOAD_AGENCY_PATH: string = 'v1/phi/document/agency/upload/org-portal/confirm/';
const ORG_PORTAL_USER_PHI_DOWNLOAD_URL_AGENCY_PATH: string =
    'v1/phi/document/agency/download/url/org-portal-user/';
const ORG_PORTAL_USER_PHI_SOFT_DELETE_AGENCY_PATH: string = 'v1/phi/document/agency/delete/org-portal-doc/';

const ListJobberPhiDocumentsAgencyUserAPICall = async (
    request: ListJobberPhiDocumentsAgencyUserRequestDTO
): Promise<PhiDocumentListRespDTO | null> => {
    const getCallInput: RemoteGETCallInputObject = {
        apiUrl: `${backendApiBasePathLocal}${LIST_JOBBER_PHI_DOCUMENTS_AGENCY_PATH}`,
        headers: {},
        queryParams: {
            jobberEmail: request.jobberEmail,
        },
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiDocumentListRespDTO, Error>> =
            await remoteCallObject.remoteGETCallV2<ServerResponse<PhiDocumentListRespDTO, Error>>(getCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error listing jobber PHI documents for agency user:', error);
        throw error;
    }
};

const InitiateAgencyJobberPhiPresignedUploadAPICall = async (
    request: AgencyJobberPhiPresignedUploadInitiateReqDTO
): Promise<PhiPresignedUploadInitiateRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<AgencyJobberPhiPresignedUploadInitiateReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${INITIATE_JOBBER_PHI_UPLOAD_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiPresignedUploadInitiateRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<PhiPresignedUploadInitiateRespDTO, Error>,
                AgencyJobberPhiPresignedUploadInitiateReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error initiating agency jobber PHI upload:', error);
        throw error;
    }
};

const ConfirmAgencyJobberPhiPresignedUploadAPICall = async (
    request: AgencyJobberPhiPresignedUploadConfirmReqDTO
): Promise<PhiPresignedUploadConfirmRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<AgencyJobberPhiPresignedUploadConfirmReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${CONFIRM_JOBBER_PHI_UPLOAD_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiPresignedUploadConfirmRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<PhiPresignedUploadConfirmRespDTO, Error>,
                AgencyJobberPhiPresignedUploadConfirmReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error confirming agency jobber PHI upload:', error);
        throw error;
    }
};

const GetJobberPhiDocumentDownloadUrlAgencyUserAPICall = async (
    request: JobberPhiPresignedDownloadUrlRequestDTO
): Promise<PhiPresignedDownloadUrlRespDTO | null> => {
    const getCallInput: RemoteGETCallInputObject = {
        apiUrl: `${backendApiBasePathLocal}${JOBBER_PHI_DOWNLOAD_URL_AGENCY_PATH}`,
        headers: {},
        queryParams: {
            jobberEmail: request.jobberEmail,
            uploadId: request.uploadId,
        },
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiPresignedDownloadUrlRespDTO, Error>> =
            await remoteCallObject.remoteGETCallV2<ServerResponse<PhiPresignedDownloadUrlRespDTO, Error>>(getCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error getting jobber PHI download URL for agency user:', error);
        throw error;
    }
};

const SoftDeleteJobberPhiDocumentAgencyUserAPICall = async (
    request: AgencyJobberPhiSoftDeleteDocumentReqDTO
): Promise<PhiSoftDeleteDocumentRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<AgencyJobberPhiSoftDeleteDocumentReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${JOBBER_PHI_SOFT_DELETE_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiSoftDeleteDocumentRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<PhiSoftDeleteDocumentRespDTO, Error>,
                AgencyJobberPhiSoftDeleteDocumentReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error soft-deleting jobber PHI document for agency user:', error);
        throw error;
    }
};

const ReviewJobberPhiDocumentAgencyUserAPICall = async (
    request: AgencyJobberPhiReviewDocumentReqDTO
): Promise<PhiReviewDocumentRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<AgencyJobberPhiReviewDocumentReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${JOBBER_PHI_REVIEW_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiReviewDocumentRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<PhiReviewDocumentRespDTO, Error>,
                AgencyJobberPhiReviewDocumentReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error reviewing jobber PHI document for agency user:', error);
        throw error;
    }
};

const ListJobberPhiDocumentViewersAgencyUserAPICall = async (
    request: JobberPhiDocumentViewersRequestDTO
): Promise<PhiDocumentViewerListRespDTO | null> => {
    const getCallInput: RemoteGETCallInputObject = {
        apiUrl: `${backendApiBasePathLocal}${JOBBER_PHI_DOCUMENT_VIEWERS_AGENCY_PATH}`,
        headers: {},
        queryParams: {
            jobberEmail: request.jobberEmail,
            fileName: request.fileName,
        },
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiDocumentViewerListRespDTO, Error>> =
            await remoteCallObject.remoteGETCallV2<ServerResponse<PhiDocumentViewerListRespDTO, Error>>(getCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error listing jobber PHI document viewers for agency user:', error);
        throw error;
    }
};

const ListTrustVaultDocumentSubTypesAgencyUserAPICall = async (): Promise<TrustVaultDocumentSubTypeListRespDTO | null> => {
    const getCallInput: RemoteGETCallInputObject = {
        apiUrl: `${backendApiBasePathLocal}${LIST_DOCUMENT_SUB_TYPES_AGENCY_PATH}`,
        headers: {},
        queryParams: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<TrustVaultDocumentSubTypeListRespDTO, Error>> =
            await remoteCallObject.remoteGETCallV2<ServerResponse<TrustVaultDocumentSubTypeListRespDTO, Error>>(getCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error listing TrustVault document sub-types for agency user:', error);
        throw error;
    }
};

const RenameTrustVaultDocumentSubTypeAgencyUserAPICall = async (
    request: RenameTrustVaultDocumentSubTypeReqDTO
): Promise<TrustVaultDocumentSubTypeRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<RenameTrustVaultDocumentSubTypeReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${RENAME_DOCUMENT_SUB_TYPE_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>,
                RenameTrustVaultDocumentSubTypeReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error renaming TrustVault document sub-type for agency user:', error);
        throw error;
    }
};

const DisableTrustVaultDocumentSubTypeAgencyUserAPICall = async (
    request: DisableTrustVaultDocumentSubTypeReqDTO
): Promise<TrustVaultDocumentSubTypeRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<DisableTrustVaultDocumentSubTypeReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${DISABLE_DOCUMENT_SUB_TYPE_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>,
                DisableTrustVaultDocumentSubTypeReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error disabling TrustVault document sub-type for agency user:', error);
        throw error;
    }
};

const CreateTrustVaultDocumentSubTypeAgencyUserAPICall = async (
    request: CreateTrustVaultDocumentSubTypeReqDTO
): Promise<TrustVaultDocumentSubTypeRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<CreateTrustVaultDocumentSubTypeReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${CREATE_DOCUMENT_SUB_TYPE_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>,
                CreateTrustVaultDocumentSubTypeReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error creating TrustVault document sub-type for agency user:', error);
        throw error;
    }
};

const ListClientPatientDocumentSubTypesAgencyUserAPICall =
    async (): Promise<TrustVaultDocumentSubTypeListRespDTO | null> => {
        const getCallInput: RemoteGETCallInputObject = {
            apiUrl: `${backendApiBasePathLocal}${LIST_CLIENT_PATIENT_DOCUMENT_SUB_TYPES_AGENCY_PATH}`,
            headers: {},
            queryParams: {},
        };

        try {
            const apiResponse: AxiosResponse<ServerResponse<TrustVaultDocumentSubTypeListRespDTO, Error>> =
                await remoteCallObject.remoteGETCallV2<
                    ServerResponse<TrustVaultDocumentSubTypeListRespDTO, Error>
                >(getCallInput);

            if (isValidWorkyServerResponseWithData(apiResponse)) {
                return apiResponse.data.data;
            }
            return null;
        } catch (error) {
            console.error('Error listing TrustVault client/patient document sub-types for agency user:', error);
            throw error;
        }
    };

const CreateClientPatientDocumentSubTypeAgencyUserAPICall = async (
    request: CreateTrustVaultDocumentSubTypeReqDTO
): Promise<TrustVaultDocumentSubTypeRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<CreateTrustVaultDocumentSubTypeReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${CREATE_CLIENT_PATIENT_DOCUMENT_SUB_TYPE_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>,
                CreateTrustVaultDocumentSubTypeReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error creating TrustVault client/patient document sub-type for agency user:', error);
        throw error;
    }
};

const DisableClientPatientDocumentSubTypeAgencyUserAPICall = async (
    request: DisableTrustVaultDocumentSubTypeReqDTO
): Promise<TrustVaultDocumentSubTypeRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<DisableTrustVaultDocumentSubTypeReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${DISABLE_CLIENT_PATIENT_DOCUMENT_SUB_TYPE_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>,
                DisableTrustVaultDocumentSubTypeReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error disabling TrustVault client/patient document sub-type for agency user:', error);
        throw error;
    }
};

const RenameClientPatientDocumentSubTypeAgencyUserAPICall = async (
    request: RenameTrustVaultDocumentSubTypeReqDTO
): Promise<TrustVaultDocumentSubTypeRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<RenameTrustVaultDocumentSubTypeReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${RENAME_CLIENT_PATIENT_DOCUMENT_SUB_TYPE_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<TrustVaultDocumentSubTypeRespDTO, Error>,
                RenameTrustVaultDocumentSubTypeReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error renaming TrustVault client/patient document sub-type for agency user:', error);
        throw error;
    }
};

const ListOrgPortalUserPhiDocumentsAgencyUserAPICall = async (
    request: ListOrgPortalUserPhiDocumentsAgencyUserRequestDTO
): Promise<PhiDocumentListRespDTO | null> => {
    const getCallInput: RemoteGETCallInputObject = {
        apiUrl: `${backendApiBasePathLocal}${LIST_ORG_PORTAL_USER_PHI_DOCUMENTS_AGENCY_PATH}`,
        headers: {},
        queryParams: {
            orgPortalUserPrimaryWorkyUserId: request.orgPortalUserPrimaryWorkyUserId,
            orgName: request.orgName,
        },
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiDocumentListRespDTO, Error>> =
            await remoteCallObject.remoteGETCallV2<ServerResponse<PhiDocumentListRespDTO, Error>>(getCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error listing org-portal user PHI documents for agency user:', error);
        throw error;
    }
};

const InitiateAgencyOrgPortalUserPhiPresignedUploadAPICall = async (
    request: AgencyOrgPortalPhiPresignedUploadInitiateReqDTO
): Promise<PhiPresignedUploadInitiateRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<AgencyOrgPortalPhiPresignedUploadInitiateReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${INITIATE_ORG_PORTAL_USER_PHI_UPLOAD_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiPresignedUploadInitiateRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<PhiPresignedUploadInitiateRespDTO, Error>,
                AgencyOrgPortalPhiPresignedUploadInitiateReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error initiating org-portal user PHI upload for agency user:', error);
        throw error;
    }
};

const ConfirmAgencyOrgPortalUserPhiPresignedUploadAPICall = async (
    request: AgencyOrgPortalPhiPresignedUploadConfirmReqDTO
): Promise<PhiPresignedUploadConfirmRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<AgencyOrgPortalPhiPresignedUploadConfirmReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${CONFIRM_ORG_PORTAL_USER_PHI_UPLOAD_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiPresignedUploadConfirmRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<PhiPresignedUploadConfirmRespDTO, Error>,
                AgencyOrgPortalPhiPresignedUploadConfirmReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error confirming org-portal user PHI upload for agency user:', error);
        throw error;
    }
};

const GetOrgPortalUserPhiDocumentDownloadUrlAgencyUserAPICall = async (
    request: OrgPortalUserPhiPresignedDownloadUrlRequestDTO
): Promise<PhiPresignedDownloadUrlRespDTO | null> => {
    const getCallInput: RemoteGETCallInputObject = {
        apiUrl: `${backendApiBasePathLocal}${ORG_PORTAL_USER_PHI_DOWNLOAD_URL_AGENCY_PATH}`,
        headers: {},
        queryParams: {
            orgPortalUserPrimaryWorkyUserId: request.orgPortalUserPrimaryWorkyUserId,
            orgName: request.orgName,
            uploadId: request.uploadId,
        },
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiPresignedDownloadUrlRespDTO, Error>> =
            await remoteCallObject.remoteGETCallV2<ServerResponse<PhiPresignedDownloadUrlRespDTO, Error>>(getCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error getting org-portal user PHI document download URL for agency user:', error);
        throw error;
    }
};

const SoftDeleteOrgPortalUserPhiDocumentAgencyUserAPICall = async (
    request: AgencyOrgPortalPhiSoftDeleteDocumentReqDTO
): Promise<PhiSoftDeleteDocumentRespDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<AgencyOrgPortalPhiSoftDeleteDocumentReqDTO> = {
        apiUrl: `${backendApiBasePathLocal}${ORG_PORTAL_USER_PHI_SOFT_DELETE_AGENCY_PATH}`,
        object: request,
        headers: {},
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<PhiSoftDeleteDocumentRespDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<PhiSoftDeleteDocumentRespDTO, Error>,
                AgencyOrgPortalPhiSoftDeleteDocumentReqDTO
            >(postCallInput);

        if (isValidWorkyServerResponseWithData(apiResponse)) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error('Error soft-deleting org-portal user PHI document for agency user:', error);
        throw error;
    }
};

export {
    ListJobberPhiDocumentsAgencyUserAPICall,
    InitiateAgencyJobberPhiPresignedUploadAPICall,
    ConfirmAgencyJobberPhiPresignedUploadAPICall,
    GetJobberPhiDocumentDownloadUrlAgencyUserAPICall,
    SoftDeleteJobberPhiDocumentAgencyUserAPICall,
    ReviewJobberPhiDocumentAgencyUserAPICall,
    ListJobberPhiDocumentViewersAgencyUserAPICall,
    ListTrustVaultDocumentSubTypesAgencyUserAPICall,
    CreateTrustVaultDocumentSubTypeAgencyUserAPICall,
    DisableTrustVaultDocumentSubTypeAgencyUserAPICall,
    RenameTrustVaultDocumentSubTypeAgencyUserAPICall,
    ListClientPatientDocumentSubTypesAgencyUserAPICall,
    CreateClientPatientDocumentSubTypeAgencyUserAPICall,
    DisableClientPatientDocumentSubTypeAgencyUserAPICall,
    RenameClientPatientDocumentSubTypeAgencyUserAPICall,
    ListOrgPortalUserPhiDocumentsAgencyUserAPICall,
    InitiateAgencyOrgPortalUserPhiPresignedUploadAPICall,
    ConfirmAgencyOrgPortalUserPhiPresignedUploadAPICall,
    GetOrgPortalUserPhiDocumentDownloadUrlAgencyUserAPICall,
    SoftDeleteOrgPortalUserPhiDocumentAgencyUserAPICall,
};
